import { Button } from '@goodboydigital/astro';
import gsap from 'gsap';
import { Container, Rectangle, Sprite } from 'pixi.js';

import { Shared } from '../../App';
import LocalisedText from '../LocalisedText';
import { Panel } from '../Panel';

const defaultOpts = {
    scale: 1,
    alpha: 1,
    accessible: false,
    accessibleTitle: 'Store Button',
    titleText: 'Select Button',
    buttonText: 'CHOOSE',
    buttonTextOffset: 0,
    tabIndex: 0,
    iconSprite: new Sprite(),
    nineSliceOffset: 50,
    flipped: false,

    hoverScale: 1.1,
    clickScale: 1.1,
};

export type StoreSelectButtonOpts = typeof defaultOpts;

export class StoreSelectButton extends Button
{
    public view: Sprite;
    public buttonText: LocalisedText;
    public originalX: number;

    protected hoverScale: number;
    protected clickScale: number;
    protected standardScale: number;
    protected itemHolder: Sprite;

    private _iconSprite: Sprite;
    private _flipped: boolean;

    constructor(opts: Partial<StoreSelectButtonOpts> = {})
    {
        const o = { ...defaultOpts, ...opts };

        const view = new Sprite();

        view.anchor.set(0.5);
        view.scale.set(o.scale);
        view.alpha = o.alpha;

        super(view, o);
        this.view = view;

        const w = 350;
        const h = 170;

        const buttonBg = new Panel({
            w,
            h,
        });

        view.addChild(buttonBg.view);

        const itemHolder = new Sprite();

        itemHolder.anchor.set(0.5);
        itemHolder.scale.set(1);
        itemHolder.addChild(o.iconSprite);
        itemHolder.x += 90;

        view.addChild(itemHolder);

        this.buttonText = new LocalisedText(o.titleText, {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 5,
            lineJoin: 'round',
            align: 'center',
            fontSize: 36,
            maxWidth: 140,
            maxHeight: 50
        });

        this.buttonText.anchor.set(0.5);
        this.buttonText.x -= 50;
        view.addChild(this.buttonText);

        this._iconSprite = o.iconSprite;
        this.itemHolder = itemHolder;

        this.hoverScale = o.hoverScale;
        this.clickScale = o.clickScale;
        this.standardScale = o.scale;

        this._flipped = false;
        if (o.flipped)
        {
            this.view.scale.x *= -1;
            itemHolder.scale.x *= -1;
            this._flipped = true;
        }

        this.view.hitArea = new Rectangle(
            -w / 2,
            -h / 2,
            w,
            h,
        );

        this.originalX = this.view.x;
    }

    public updateIcon(newIcon: Container): void
    {
        this._iconSprite.removeChildren();
        this._iconSprite.addChild(newIcon);
        
        this.buttonText.scale.x = this._flipped ? -Math.abs(this.buttonText.scale.x) : Math.abs(this.buttonText.scale.x);
    }

    public down(): void
    {
        gsap.killTweensOf(this.view.scale);
        gsap.killTweensOf(this.view);

        gsap.to(this.itemHolder.scale, {
            duration: 0.14,
            x: this._flipped ? -this.clickScale : this.clickScale,
            y: this.clickScale,
            ease: 'back.out',
        });

        gsap.to(this.view, {
            duration: 0.8,
            x: this._flipped ? this.view.x - 10 : this.view.x + 10,
            ease: 'elastic.out(1.6, 0.3)',
        });

        Shared.game.soundSystem.playSfx('btn-click');
    }

    public up(): void
    {
        gsap.killTweensOf(this.itemHolder.scale);
        gsap.killTweensOf(this.view);

        gsap.to(this.itemHolder.scale, {
            duration: 0.3,
            x: this._flipped ? -this.standardScale : this.standardScale,
            y: this.standardScale,
            ease: 'back.inOut(2)',
        });

        gsap.to(this.view, {
            duration: 0.8,
            x: this.originalX,
            ease: 'elastic.out(1.6, 0.3)',
        });
    }

    public hover(): void
    {
        gsap.killTweensOf(this.itemHolder.scale);
        gsap.killTweensOf(this.view);

        gsap.to(this.itemHolder.scale, {
            duration: 0.8,
            x: this._flipped ? -this.hoverScale : this.hoverScale,
            y: this.hoverScale,
            ease: 'elastic.out(1.6, 0.3)',
        });

        gsap.to(this.view, {
            duration: 0.8,
            x: this._flipped ? this.view.x - 10 : this.view.x + 10,
            ease: 'elastic.out(1.6, 0.3)',
        });

        Shared.game.soundSystem.playSfx('btn-hover');
    }
}
